// Every product, by category name, with its Autotask and OneBill state and the
// Dealers see switch. A strip filter narrows it; a category picks one card.
import React, { useState } from "react";
import { formatCents } from "@phoneware/lens-domain";
import { Icon } from "../../icons.js";
import { plural } from "../../format.js";
import { Chip } from "../../ui.js";
import { useCatalog } from "./context.js";
import { FILTERS, FILTER_EMPTY, FILTER_NAME, billedText, blockers, categoriesOf, kindWord, type FilterId, type Product } from "./model.js";
import { AtChip, LinkChip } from "./parts.js";
import { SyncPanel } from "./SyncPanel.js";

function Row({ p }: { p: Product }): React.ReactElement {
  const { open, enable, busy, links, manages } = useCatalog();
  const reasons = blockers(p);
  return (
    <tr
      className={p.active ? "go" : "go dim"}
      data-testid={`catalog-row-${p.id}`}
      onClick={(e) => {
        if (!(e.target as HTMLElement).closest("label, button")) open(p.id);
      }}
    >
      <td>
        <span className="pname">{p.name}</span>
        <span className="code">
          {p.autotask ? `${kindWord(p)} ${p.autotask.id}` : `New ${kindWord(p).toLowerCase()}`}
          {p.code ? ` \u00b7 ${p.code}` : ""}
        </span>
        {p.billing ? null : <span className="why">{billedText(p)}</span>}
        {p.active ? null : <span className="sub">Inactive</span>}
        <span className="chipcol sm-only">
          <AtChip p={p} />
          <LinkChip p={p} />
        </span>
      </td>
      <td className="hide-sm">
        {p.billing ? (
          <span>{billedText(p)}</span>
        ) : (
          <Chip kind="warn" icon="clock">
            {billedText(p)}
          </Chip>
        )}
      </td>
      <td className="num">{formatCents(p.listPrice)}</td>
      <td className="num hide-sm">{formatCents(p.floorPrice)}</td>
      <td className="hide-sm">
        <span className="chipcol">
          <AtChip p={p} testId={`catalog-at-${p.id}`} />
          <LinkChip p={p} testId={`catalog-link-${p.id}`} />
        </span>
      </td>
      <td>
        <label className="switch" title={reasons.length ? `Can\u2019t switch on: ${reasons.join(", ")}` : "Dealers see it"}>
          <input type="checkbox" aria-label={`Dealers see ${p.name}`} data-testid={`catalog-enable-${p.id}`} checked={p.enabled} disabled={busy || !manages} onChange={(e) => void enable(p, e.target.checked, "row")} />
          <i />
        </label>
      </td>
      <td className="num hide-sm">
        <button className="btn small" type="button" data-testid={`catalog-open-${p.id}`} onClick={() => open(p.id)}>
          {!links ? "Open" : p.billingLink.state === "unreviewed" ? "Link" : p.autotaskSync.state === "changed_in_autotask" ? "Settle" : "Open"}
        </button>
      </td>
    </tr>
  );
}

function CategoryCard({ name, items, whole }: { name: string; items: Product[]; whole: boolean }): React.ReactElement {
  const [open, setOpen] = useState(false);
  const rows = whole || open ? items : items.slice(0, 6);
  return (
    <section className="card" data-testid={`cat-${name}`}>
      <div className="card-h">
        <h2>{name}</h2>
        <span className="note">{plural(items.length, "product")}</span>
      </div>
      <div className="tablewrap">
        <table className="t t-cat t-ops">
          <thead>
            <tr>
              <th>Product</th>
              <th className="hide-sm">Billed</th>
              <th className="num">List</th>
              <th className="num hide-sm">Floor</th>
              <th className="hide-sm">Autotask · OneBill</th>
              <th>
                <span className="hide-sm">Dealers see</span>
                <span className="sm-only">On</span>
              </th>
              <th className="hide-sm">
                <span className="sr">Open</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <Row key={p.id} p={p} />
            ))}
            {rows.length < items.length ? (
              <tr className="more-row">
                <td colSpan={7}>
                  <button className="btn small" type="button" data-testid={`cat-more-${name}`} onClick={() => setOpen(true)}>
                    Show all {items.length}
                  </button>
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export function ProductsTab({ filter, onFilter, onReview }: { filter: FilterId | ""; onFilter: (f: FilterId | "") => void; onReview: () => void }): React.ReactElement {
  const { list, open, links } = useCatalog();
  const [category, setCategory] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  let shown = filter ? list.filter(FILTERS[filter]) : list.filter((p) => showInactive || p.active);
  if (category) shown = shown.filter((p) => p.category === category);
  const inactive = list.filter((p) => !p.active).length;
  const cards = categoriesOf(list).filter((c) => shown.some((p) => p.category === c));

  return (
    <>
      {filter ? (
        <div className="fbanner" data-testid="filter-banner">
          <Icon name="info" size={15} />
          <span>
            Showing <b>{plural(shown.length, "product")}</b> {FILTER_NAME[filter]}.
          </span>
          {filter === "needs" ? (
            <button className="btn small" type="button" onClick={onReview}>
              Work through the review queue
            </button>
          ) : null}
          <button className="link" type="button" data-testid="filter-clear" onClick={() => onFilter("")}>
            Show all
          </button>
        </div>
      ) : null}
      {filter === "create" && links ? <SyncPanel onOpen={open} /> : null}
      <div className="bar">
        <select className="input slim cat-sel" aria-label="Category" data-testid="cat-select" value={category} onChange={(e) => setCategory(e.target.value)}>
          <option value="">All categories</option>
          {categoriesOf(list).map((c) => (
            <option key={c}>{c}</option>
          ))}
        </select>
        {filter ? null : (
          <label className="tog">
            <input type="checkbox" data-testid="show-inactive" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} /> Show {inactive} inactive
          </label>
        )}
      </div>
      {cards.length === 0 ? (
        <section className="card">
          <div className="empty" data-testid="filter-empty">
            {filter ? FILTER_EMPTY[filter] : "No products here."}
          </div>
        </section>
      ) : (
        <div className="stack">
          {cards.map((c) => (
            <CategoryCard key={c} name={c} items={shown.filter((p) => p.category === c)} whole={filter !== "" || category !== ""} />
          ))}
        </div>
      )}
    </>
  );
}
